import { createContext, type ReactNode, useCallback, useContext, useMemo, useState } from "react";

/**
 * Signals that the user arrived in this chat to update an EXISTING micro app's
 * content (via the viewer's "Edit in chat" deep-link) rather than to publish a
 * new one. When set, the artifact toolbar's publish control switches to an
 * "Update <app>" action that republishes to the target app instead of forking.
 *
 * Also tracks which artifact (by attachment UUID) maps to a published micro app
 * — populated when a dashboard is published/updated in this session — so the
 * HTML preview panel can hydrate live data and offer an "Open micro app" link.
 */
interface MicroAppUpdateTarget {
  appUuid: string;
  appName: string;
}

interface MicroAppUpdateContextValue {
  target: MicroAppUpdateTarget | null;
  setTarget: (target: MicroAppUpdateTarget | null) => void;
  /** Published app UUID for a given source attachment, if published this session. */
  publishedAppFor: (attachmentUuid: string | undefined) => string | undefined;
  /** Record that an attachment was published/updated into the given app. */
  registerPublishedApp: (attachmentUuid: string, appUuid: string) => void;
}

const MicroAppUpdateContext = createContext<MicroAppUpdateContextValue | null>(null);

const NOOP_VALUE: MicroAppUpdateContextValue = {
  target: null,
  setTarget: () => {},
  publishedAppFor: () => undefined,
  registerPublishedApp: () => {},
};

export function MicroAppUpdateProvider({
  initialTarget = null,
  children,
}: Readonly<{ initialTarget?: MicroAppUpdateTarget | null; children: ReactNode }>) {
  const [target, setTarget] = useState<MicroAppUpdateTarget | null>(initialTarget);
  const [publishedApps, setPublishedApps] = useState<Record<string, string>>({});

  const registerPublishedApp = useCallback((attachmentUuid: string, appUuid: string) => {
    setPublishedApps((prev) => ({ ...prev, [attachmentUuid]: appUuid }));
  }, []);
  const publishedAppFor = useCallback(
    (attachmentUuid: string | undefined) => (attachmentUuid ? publishedApps[attachmentUuid] : undefined),
    [publishedApps],
  );

  const value = useMemo(
    () => ({ target, setTarget, publishedAppFor, registerPublishedApp }),
    [target, publishedAppFor, registerPublishedApp],
  );
  return <MicroAppUpdateContext.Provider value={value}>{children}</MicroAppUpdateContext.Provider>;
}

/** Read the active micro-app update state. Safe outside the provider. */
export function useMicroAppUpdateTarget(): MicroAppUpdateContextValue {
  return useContext(MicroAppUpdateContext) ?? NOOP_VALUE;
}
